import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Divider,
  Pagination
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { api } from '../../services/api';

// Interfaces
interface UserBasicInfo {
  user_id: number;
  username: string;
  email_hash: string;
  status: string;
  kyc_status: string;
  created_at: string;
  updated_at: string;
}

interface WalletBalance {
  amount: string;
  assetCode: string;
  assetName: string;
  status: string;
  blockchainAssetId: number;
}

interface Wallet {
  walletId: number;
  address: string;
  blockchainId: number;
  walletType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  balances: WalletBalance[];
}

interface Transaction {
  transactionId: number;
  walletId: number;
  extUid?: string;
  amount: string;
  assetCode: string;
  assetName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface CustodyExchange {
  exchangeId: number;
  extId: string;
  fromAssetId: number;
  toAssetId: number;
  fromAmount: string;
  toAmount: string;
  rate: string;
  status: string;
  blockchainName: string;
  createdAt: string;
  updatedAt: string;
  fromAssetCode: string;
  fromAssetName: string;
  toAssetCode: string;
  toAssetName: string;
}

// Update UserDetailsData interface
interface UserDetailsData {
  user: UserBasicInfo;
  wallets: Wallet[];
  withdrawalHistory: Transaction[];
  depositHistory: Transaction[];
  exchanges: CustodyExchange[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Update the interface to match the API response
interface ApiResponse {
  success: boolean;
  data: UserDetailsData;
}

interface ConsolidatedBalance {
  assetCode: string;
  assetName: string;
  totalAmount: string;
}

const consolidateBalances = (wallets: Wallet[]): ConsolidatedBalance[] => {
  const balanceMap = new Map<string, ConsolidatedBalance>();
  
  wallets.forEach(wallet => {
    wallet.balances.forEach(balance => {
      const existing = balanceMap.get(balance.assetCode);
      if (existing) {
        existing.totalAmount = (parseFloat(existing.totalAmount) + parseFloat(balance.amount)).toString();
      } else {
        balanceMap.set(balance.assetCode, {
          assetCode: balance.assetCode,
          assetName: balance.assetName,
          totalAmount: balance.amount
        });
      }
    });
  });
  
  return Array.from(balanceMap.values());
};

const UserDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || '0');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);

  // Status rendering functions
  const renderUserStatus = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Активный" color="success" size="small" />;
      case 'inactive':
        return <Chip label="Приостановлен" color="warning" size="small" />;
      case 'pending':
        return <Chip label="Заблокирован" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const renderKycStatus = (status: string) => {
    switch (status) {
      case 'approved':
        return <Chip label="Подтвержден" color="success" size="small" />;
      case 'pending':
        return <Chip label="На проверке" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="Отклонен" color="error" size="small" />;
      case 'not_verified':
        return <Chip label="Не верифицирован" color="default" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const renderWalletStatus = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Активный" color="success" size="small" />;
      case 'inactive':
        return <Chip label="Неактивный" color="warning" size="small" />;
      case 'blocked':
        return <Chip label="Заблокирован" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const renderTransactionStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Завершена" color="success" size="small" />;
      case 'pending':
        return <Chip label="В обработке" color="warning" size="small" />;
      case 'failed':
        return <Chip label="Неуспешна" color="error" size="small" />;
      case 'cancelled':
        return <Chip label="Отменена" color="default" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  // Add renderExchangeStatus function
  const renderExchangeStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Завершен" color="success" size="small" />;
      case 'processing':
        return <Chip label="В обработке" color="warning" size="small" />;
      case 'created':
        return <Chip label="Создан" color="info" size="small" />;
      case 'failed':
        return <Chip label="Неуспешен" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  // Update the fetchUserDetails function
  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [userResponse, exchangesResponse] = await Promise.all([
        api.get<ApiResponse>(`/api/v1/admin-panel/users/${userId}`),
        api.get<{success: boolean; data: {success: boolean; data: {exchanges: CustodyExchange[]}}}>(`/api/v1/admin-panel/users/${userId}/exchanges`)
      ]);
      
      console.log('User Response:', userResponse.data);
      console.log('Exchanges Response:', exchangesResponse.data);
      
      if (userResponse.data.success && userResponse.data.data) {
        // The exchanges response has a nested data.data structure
        const exchanges = exchangesResponse.data?.data?.data?.exchanges || [];
        
        setUserDetails({
          ...userResponse.data.data,
          exchanges
        });
      } else {
        setError('Не удалось загрузить данные пользователя');
      }
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке данных пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    fetchUserDetails();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!userDetails) {
    return (
      <Alert severity="warning">
        Пользователь с ID {id} не найден.
      </Alert>
    );
  }

  const { user, wallets, withdrawalHistory, depositHistory } = userDetails;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Пользователь: {user.username}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Обновить
        </Button>
      </Box>

      {/* User Basic Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} />
          Основная информация
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">ID пользователя</Typography>
            <Typography variant="body1">{user.user_id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Имя пользователя</Typography>
            <Typography variant="body1">{user.username}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Email (хэш)</Typography>
            <Typography variant="body1">{user.email_hash}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Статус</Typography>
            <Box sx={{ mt: 0.5 }}>
              {renderUserStatus(user.status)}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">KYC статус</Typography>
            <Box sx={{ mt: 0.5 }}>
              {renderKycStatus(user.kyc_status)}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Дата регистрации</Typography>
            <Typography variant="body1">
              {format(new Date(user.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="user data tabs">
            <Tab 
              label="БАЛАНС КОШЕЛЬКА" 
              icon={<AccountBalanceWalletIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="История выводов" 
              icon={<HistoryIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="История вводов" 
              icon={<HistoryIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Обмены" 
              icon={<HistoryIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Wallets Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            {wallets.length === 0 ? (
              <Alert severity="info">
                У пользователя нет кошельков.
              </Alert>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    БАЛАНС КОШЕЛЬКА
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  {consolidateBalances(wallets).map((balance, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2,
                      p: 1,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}>
                      <Typography variant="body1">
                        {balance.assetName} ({balance.assetCode})
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {balance.totalAmount}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>

        {/* Withdrawal History Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            {withdrawalHistory.length === 0 ? (
              <Alert severity="info">
                У пользователя нет истории выводов.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID транзакции</TableCell>
                      <TableCell>Кошелек</TableCell>
                      <TableCell>Сумма</TableCell>
                      <TableCell>Актив</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawalHistory.map((transaction) => (
                      <TableRow key={transaction.transactionId}>
                        <TableCell>{transaction.transactionId}</TableCell>
                        <TableCell>
                          {transaction.walletId ? `#${transaction.walletId}` : 'Н/Д'}
                        </TableCell>
                        <TableCell>{transaction.amount}</TableCell>
                        <TableCell>
                          {transaction.assetName} ({transaction.assetCode})
                        </TableCell>
                        <TableCell>
                          {renderTransactionStatus(transaction.status)}
                        </TableCell>
                        <TableCell>
                          {format(new Date(transaction.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Deposit History Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            {depositHistory.length === 0 ? (
              <Alert severity="info">
                У пользователя нет истории вводов.
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID операции</TableCell>
                        <TableCell>Внешний ID</TableCell>
                        <TableCell>Сумма</TableCell>
                        <TableCell>Актив</TableCell>
                        <TableCell>Кошелек</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Дата создания</TableCell>
                        <TableCell>Дата обновления</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {depositHistory.map((deposit) => (
                        <TableRow key={deposit.transactionId}>
                          <TableCell>{deposit.transactionId}</TableCell>
                          <TableCell>{deposit.extUid || 'N/A'}</TableCell>
                          <TableCell>{deposit.amount}</TableCell>
                          <TableCell>
                            {deposit.assetCode} ({deposit.assetName})
                          </TableCell>
                          <TableCell>{deposit.walletId}</TableCell>
                          <TableCell>
                            {renderTransactionStatus(deposit.status)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(deposit.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                          </TableCell>
                          <TableCell>
                            {format(new Date(deposit.updatedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </TabPanel>

        {/* Exchanges Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            {!userDetails.exchanges || userDetails.exchanges.length === 0 ? (
              <Alert severity="info">
                У пользователя нет обменов.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID обмена</TableCell>
                      <TableCell>Внешний ID</TableCell>
                      <TableCell>Из</TableCell>
                      <TableCell>В</TableCell>
                      <TableCell>Сумма</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата создания</TableCell>
                      <TableCell>Дата обновления</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userDetails.exchanges.map((exchange) => (
                      <TableRow key={exchange.exchangeId}>
                        <TableCell>{exchange.exchangeId}</TableCell>
                        <TableCell>{exchange.extId || 'N/A'}</TableCell>
                        <TableCell>
                          {exchange.fromAssetName} ({exchange.fromAssetCode})
                        </TableCell>
                        <TableCell>
                          {exchange.toAssetName} ({exchange.toAssetCode})
                        </TableCell>
                        <TableCell>{exchange.fromAmount}</TableCell>
                        <TableCell>
                          {renderExchangeStatus(exchange.status)}
                        </TableCell>
                        <TableCell>
                          {format(new Date(exchange.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                        </TableCell>
                        <TableCell>
                          {format(new Date(exchange.updatedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default UserDetailsPage; 